/** Build the post-OAuth / magic-link return URL handled by `/auth/callback`. */
export function buildAuthCallbackUrl(nextPath: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  const next = sanitizeNextPath(nextPath)
  return `${origin}/auth/callback?next=${encodeURIComponent(next)}`
}

export function sanitizeNextPath(raw: string | null | undefined, fallback = "/dashboard/think"): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return fallback
  return raw
}

export type AuthUrlError = {
  code: string | null
  description: string | null
  error: string
}

/** Read OAuth/GoTrue errors from query string and/or hash fragment. */
export function readAuthUrlError(
  search?: string | null,
  hash?: string | null,
): AuthUrlError | null {
  const fromSearch = new URLSearchParams(search ?? "")
  const fromHash = new URLSearchParams((hash ?? "").replace(/^#/, ""))
  const error = fromSearch.get("error") ?? fromHash.get("error")
  if (!error) return null
  return {
    code: fromSearch.get("error_code") ?? fromHash.get("error_code"),
    description:
      fromSearch.get("error_description") ?? fromHash.get("error_description"),
    error,
  }
}

export function authErrorToQuery(err: AuthUrlError): string {
  const q = new URLSearchParams()
  q.set("error", err.error)
  if (err.code) q.set("error_code", err.code)
  if (err.description) q.set("error_description", err.description)
  return q.toString()
}

/** User-facing French copy for known GoTrue / Google OAuth failures. */
export function formatAuthErrorMessage(err: AuthUrlError): string {
  const description = (err.description ?? "").replace(/\+/g, " ")
  const lower = `${err.error} ${err.code ?? ""} ${description}`.toLowerCase()

  if (
    lower.includes("invalid_client") ||
    lower.includes("unable to exchange external code") ||
    lower.includes("exchange external code")
  ) {
    return "Connexion Google impossible : le serveur n’a pas pu échanger le code OAuth (secret client Google invalide ou mal configuré). Utilisez email / mot de passe en attendant, ou contactez le support."
  }
  if (lower.includes("access_denied")) {
    return "Connexion annulée. Vous pouvez réessayer quand vous voulez."
  }
  if (description.trim()) return description
  if (err.code) return `Erreur ${err.code}`
  return "La connexion a échoué. Réessayez ou utilisez un autre mode de connexion."
}
